import { useCallback, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { graphql } from '@/gql';
import {
  EMAIL_UNDELIVERABLE,
  graphqlErrorCode,
  graphqlErrorDomain,
  graphqlErrorSuggestion,
} from '@/lib/graphqlError';

const CheckEmailAddressDoc = graphql(/* GraphQL */ `
  query CheckEmailAddress($email: String!) {
    checkEmailAddress(email: $email) {
      deliverable
      suggestion
    }
  }
`);

/**
 * Asks Go whether a typed address looks like a typo (`checkEmailAddress`) and
 * resolves the suggested whole address, or `null` for "nothing to ask".
 *
 * Call `check` from a submit handler only, never from onChange / onBlur: the
 * API scales to zero and the query is rate-limited per IP. Every failure
 * (rate limit, network, malformed address) resolves `null` so the probe can
 * never block a form. An undeliverable domain (`deliverable: false`) also
 * resolves `null`: Go owns that rule, the mutation refuses it with
 * `EMAIL_UNDELIVERABLE` and `useEmailGuard.refused` shows Go's message.
 */
export function useEmailCheck() {
  const client = useApolloClient();
  const check = useCallback(
    async (email: string): Promise<string | null> => {
      try {
        const { data } = await client.query({
          query: CheckEmailAddressDoc,
          variables: { email },
          fetchPolicy: 'no-cache',
        });
        const result = data?.checkEmailAddress;
        return result?.deliverable ? (result.suggestion ?? null) : null;
      } catch {
        return null;
      }
    },
    [client],
  );
  return { check };
}

export interface EmailPrompt {
  typed: string;
  suggestion: string;
  /** True when Go refused the address (`EMAIL_UNDELIVERABLE`): only Use is offered. */
  refused: boolean;
}

/**
 * Submit-time "Did you mean" state for ONE address field. A form with two
 * address fields uses two guards (see `screenAll`).
 *
 * - `screen(email)` runs in the submit handler: `false` means stop before the
 *   mutation (the prompt is showing, or a probe for this field is already in
 *   flight and this submit is ignored).
 * - `answer('use' | 'keep')` answers the prompt and returns the address to
 *   submit; the value is marked confirmed so the next submit does not ask again.
 * - `refused(err, typed)` goes in the mutation's catch block: for an
 *   `EMAIL_UNDELIVERABLE` refusal of this field's domain that carries a
 *   suggestion it shows the Use-only prompt. The form still shows Go's message
 *   as its usual error.
 * - `changed()` goes in the field's onChange: it drops the prompt and the
 *   confirmation. It never touches the network.
 */
export function useEmailGuard() {
  const { check } = useEmailCheck();
  const [prompt, setPrompt] = useState<EmailPrompt | null>(null);
  const confirmed = useRef<string | null>(null);
  const edits = useRef(0);
  const probing = useRef(false);

  const changed = useCallback(() => {
    edits.current += 1;
    confirmed.current = null;
    setPrompt(null);
  }, []);

  const screen = useCallback(
    async (email: string): Promise<boolean> => {
      if (confirmed.current === email) return true;
      // A second submit (Enter again) while the probe is in flight is ignored,
      // so a slow DNS answer can never double-post the form.
      if (probing.current) return false;
      probing.current = true;
      try {
        const edit = edits.current;
        const suggestion = await check(email);
        if (edit !== edits.current) return false;
        if (!suggestion) {
          setPrompt(null);
          return true;
        }
        setPrompt({ typed: email, suggestion, refused: false });
        return false;
      } finally {
        probing.current = false;
      }
    },
    [check],
  );

  const answer = useCallback(
    (kind: 'use' | 'keep'): string | null => {
      if (!prompt) return null;
      const address = kind === 'use' ? prompt.suggestion : prompt.typed;
      confirmed.current = address;
      setPrompt(null);
      return address;
    },
    [prompt],
  );

  const refused = useCallback((err: unknown, typed: string): void => {
    if (graphqlErrorCode(err) !== EMAIL_UNDELIVERABLE) return;
    // A form with two address fields: only the field whose domain was refused reacts.
    const domain = graphqlErrorDomain(err);
    if (
      domain &&
      domain.toLowerCase() !==
        typed.slice(typed.lastIndexOf('@') + 1).toLowerCase()
    ) {
      return;
    }
    confirmed.current = null;
    const suggestion = graphqlErrorSuggestion(err);
    setPrompt(suggestion ? { typed, suggestion, refused: true } : null);
  }, []);

  return { prompt, screen, answer, refused, changed };
}

export type EmailGuard = ReturnType<typeof useEmailGuard>;

/**
 * Screens every address of one submit (guard + address; a null address is
 * skipped, e.g. an empty or unchanged one). Every address is probed so each
 * prompt shows under its own field. True when all may be submitted.
 */
export async function screenAll(
  entries: ReadonlyArray<readonly [EmailGuard, string | null]>,
): Promise<boolean> {
  let ok = true;
  for (const [guard, address] of entries) {
    if (address && !(await guard.screen(address))) ok = false;
  }
  return ok;
}

/** Calls `refused` on each guard for a failed mutation (every guard filters by its own domain). */
export function refuseAll(
  err: unknown,
  entries: ReadonlyArray<readonly [EmailGuard, string]>,
): void {
  for (const [guard, typed] of entries) guard.refused(err, typed);
}
